import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Zap, Users, FileStack, ShieldCheck } from "lucide-react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

export default function Pricing() {
  const [data, setData] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/pricing").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const tierBadge = {
    Basic: "text-emerald-700 bg-emerald-50 border-emerald-200",
    Standard: "text-indigo-700 bg-indigo-50 border-indigo-200",
    Advanced: "text-amber-700 bg-amber-50 border-amber-200",
  };

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
            <Zap className="h-3.5 w-3.5 text-amber-400" /> Transparent Pricing
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Fair, upfront credit pricing</h1>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            Simple jobs cost less, heavy jobs cost a little more — and you always see the exact cost before you spend a credit. No subscriptions, credits never expire.
          </p>
        </div>

        {/* Tiers */}
        <div className="mt-12 grid md:grid-cols-3 gap-6" data-testid="pricing-tiers">
          {(data?.tiers || []).map((t) => {
            return (
              <div key={t.name} className="bg-white rounded-2xl border border-slate-200 p-7 flex flex-col">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold w-fit border ${tierBadge[t.name] || "text-slate-700 bg-slate-50 border-slate-200"}`}>
                  {t.name}
                </div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-bold">{t.credits}</span>
                  <span className="text-slate-500 text-sm">credit{t.credits > 1 ? "s" : ""}</span>
                </div>
                <ul className="mt-5 space-y-2 text-sm text-slate-600 flex-1">
                  {t.items.map((it) => (
                    <li key={it} className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {it}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Overage + team */}
        <div className="mt-6 grid sm:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0"><FileStack className="h-5 w-5 text-indigo-600" /></div>
            <div>
              <h3 className="font-display font-semibold">Large documents</h3>
              <p className="text-slate-600 text-sm mt-1">{data?.overage?.note}</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0"><Users className="h-5 w-5 text-indigo-600" /></div>
            <div>
              <h3 className="font-display font-semibold">Team workspaces</h3>
              <p className="text-slate-600 text-sm mt-1">Exports from a shared team pool add a <strong>+{data?.team_premium ?? 1} credit</strong> collaboration premium on top of the tier cost.</p>
            </div>
          </div>
        </div>

        {/* Supported / not supported */}
        <div className="mt-6 grid sm:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="pricing-supported">
            <h3 className="font-display font-semibold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-500" /> What you can do</h3>
            <div className="mt-4 space-y-4 text-sm">
              {data?.supported && Object.entries({
                "Convert to PDF": data.supported.to_pdf,
                "Create from PDF": data.supported.from_pdf,
                "Other tools": data.supported.other,
              }).map(([label, items]) => (
                <div key={label}>
                  <p className="text-xs uppercase tracking-widest text-slate-400 font-mono mb-1">{label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {items.map((it) => (
                      <span key={it} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-full text-xs">{it}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="pricing-limits">
            <h3 className="font-display font-semibold flex items-center gap-2"><X className="h-5 w-5 text-red-500" /> Current limits</h3>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">
              {(data?.not_supported || []).map((it) => (
                <li key={it} className="flex items-start gap-2"><X className="h-4 w-4 text-red-400 mt-0.5 shrink-0" /> {it}</li>
              ))}
            </ul>
            <p className="text-xs text-slate-400 mt-4">Max file size: <strong>{data?.max_upload_mb} MB</strong> per upload.</p>
          </div>
        </div>

        {/* Packs CTA */}
        <div className="mt-10 bg-slate-900 rounded-3xl px-8 py-12 text-center text-white">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Buy credits, use them anytime</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            {(data?.packs || []).map((p) => (
              <div key={p.lookup_key} className="bg-white/5 border border-white/10 rounded-2xl px-6 py-5 min-w-[150px]">
                <div className="font-display text-3xl font-bold">${p.price}</div>
                <div className="text-slate-300 text-sm mt-1 font-mono">{p.credits} credits</div>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link to={user ? "/buy-credits" : "/"} data-testid="pricing-buy-cta">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8 h-12 text-base">
                {user ? "Buy credits" : "Get started free"}
              </Button>
            </Link>
          </div>
          <p className="text-slate-400 text-xs mt-4">New accounts get 3 free credits.</p>
        </div>
      </div>
    </div>
  );
}
